import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Table,
  TableHeader,
  TableCell,
  TableRow,
  TitleCell,
} from "./Fans.style";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { useNavigate } from "react-router-dom";
import { getUserFans } from "../../api/project";
import * as XLSX from "xlsx";

const text = (value) => String(value ?? "").trim();
const fmt = (value) => Number(value || 0).toLocaleString("ja-JP");
const compareText = (a, b) =>
  text(a).localeCompare(text(b), "ja-JP", {
    numeric: true,
    sensitivity: "base",
  });
const matches = (value, keyword) => {
  const q = text(keyword);
  if (!q) return true;
  if (q === "無") return !text(value);
  return text(value)
    .toLocaleLowerCase("ja-JP")
    .includes(q.toLocaleLowerCase("ja-JP"));
};
const kanaKeyword = (item) =>
  [text(item.userKana), text(item.searchKeyword)].filter(Boolean).join("／");
const BUCKETS = [
  { label: "1人", test: (n) => n === 1 },
  { label: "2～5人", test: (n) => n >= 2 && n <= 5 },
  { label: "6～10人", test: (n) => n >= 6 && n <= 10 },
  { label: "11～30人", test: (n) => n >= 11 && n <= 30 },
  { label: "31人以上", test: (n) => n >= 31 },
];

const Fans = () => {
  const [infoList, setInfoList] = useState([]);
  const [filterUserName, setFilterUserName] = useState("");
  const [filterKanaSearchKeyWord, setFilterKanaSearchKeyWord] = useState("");
  const [filterFanName, setFilterFanName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [chartsVisible, setChartsVisible] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem("userId")) {
      navigate("/");
      return;
    }
    let alive = true;
    const load = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const res = await getUserFans();
        if (!alive) return;
        if (res?.code !== 200 || !Array.isArray(res.data)) {
          throw new Error("データの取得に失敗しました");
        }
        setInfoList(res.data);
      } catch (error) {
        if (alive)
          setErrorMessage(error?.message || "データの取得に失敗しました");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [navigate]);

  // Preserve Pro/Guest groups. Filtering a fan name only narrows the members inside each group.
  const filteredList = useMemo(
    () =>
      infoList
        .filter((item) => matches(item.user_name, filterUserName))
        .filter((item) => {
          const q = text(filterKanaSearchKeyWord);
          if (!q) return true;
          if (q === "無")
            return !text(item.userKana) && !text(item.searchKeyword);
          return matches(item.userKana, q) || matches(item.searchKeyword, q);
        })
        .map((item) => ({
          ...item,
          fansList: (Array.isArray(item.fansList) ? item.fansList : []).filter(
            (fan) => matches(fan?.fanName, filterFanName),
          ),
        }))
        .filter((item) => item.fansList.length > 0),
    [infoList, filterUserName, filterKanaSearchKeyWord, filterFanName],
  );

  const sortedList = useMemo(() => {
    if (!sortColumn) return filteredList;
    const direction = sortDirection === "asc" ? 1 : -1;
    const result = filteredList.map((item) => ({
      ...item,
      fansList:
        sortColumn === "fanName"
          ? [...item.fansList].sort(
              (a, b) => compareText(a?.fanName, b?.fanName) * direction,
            )
          : item.fansList,
    }));
    result.sort((a, b) => {
      if (sortColumn === "user_name")
        return compareText(a.user_name, b.user_name) * direction;
      if (sortColumn === "kana")
        return compareText(kanaKeyword(a), kanaKeyword(b)) * direction;
      if (sortColumn === "fanName")
        return (
          compareText(a.fansList[0]?.fanName, b.fansList[0]?.fanName) *
          direction
        );
      return 0;
    });
    return result;
  }, [filteredList, sortColumn, sortDirection]);

  // Business rule: fanName is globally unique, so the same name identifies the same fan.
  // Counts are based on the current filtered favourite relationships.
  const analytics = useMemo(() => {
    const allFans = filteredList.flatMap((item) => item.fansList);
    const fanNames = new Map();
    for (const fan of allFans) {
      const name = text(fan?.fanName);
      if (name) {
        const key = name;
        const old = fanNames.get(key);
        fanNames.set(key, {
          name: old?.name || name,
          count: (old?.count || 0) + 1,
        });
      }
    }
    const groupCounts = filteredList.map((item) => ({
      name: text(item.user_name) || text(item.user_id) || "未設定",
      userId: text(item.user_id),
      count: item.fansList.length,
    }));
    const total = allFans.length;
    const uniqueFans = fanNames.size;
    return {
      total,
      groups: filteredList.length,
      averagePerPro: filteredList.length
        ? (total / filteredList.length).toFixed(1)
        : "0.0",
      averagePerFan: uniqueFans ? (total / uniqueFans).toFixed(1) : "0.0",
      uniqueFans,
      unnamed: allFans.filter((fan) => !text(fan?.fanName)).length,
      topPros: [...groupCounts]
        .sort((a, b) => b.count - a.count || compareText(a.name, b.name))
        .slice(0, 10),
      buckets: BUCKETS.map((bucket) => ({
        name: bucket.label,
        count: groupCounts.filter((item) => bucket.test(item.count)).length,
      })),
      topFans: [...fanNames.values()]
        .sort((a, b) => b.count - a.count || compareText(a.name, b.name))
        .slice(0, 10),
    };
  }, [filteredList]);

  const totalFans = useMemo(
    () =>
      infoList.reduce(
        (sum, item) =>
          sum + (Array.isArray(item.fansList) ? item.fansList.length : 0),
        0,
      ),
    [infoList],
  );
  const totalPages = Math.max(1, Math.ceil(sortedList.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedList = sortedList.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterUserName, filterKanaSearchKeyWord, filterFanName]);
  const handleSort = (column) => {
    if (sortColumn === column)
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };
  const sortIcon = (column) =>
    sortColumn === column ? (sortDirection === "asc" ? " ▲" : " ▼") : " ↕";
  const clear = () => {
    setFilterUserName("");
    setFilterKanaSearchKeyWord("");
    setFilterFanName("");
  };
  const exportToExcel = () => {
    const rows = sortedList.flatMap((item) =>
      item.fansList.map((fan) => ({
        "プロ／ゲスト": text(item.user_name),
        "カナ／検索キーワード": kanaKeyword(item),
        ファン名: text(fan?.fanName),
      })),
    );
    if (!rows.length) return;
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 24 }, { wch: 34 }, { wch: 28 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "お気に入り一覧");
    XLSX.writeFile(workbook, "お気に入り一覧.xlsx");
  };

  const Pagination = () => (
    <div className="fans-pagination" aria-label="ページ操作">
      <button
        type="button"
        onClick={() => setCurrentPage(1)}
        disabled={safePage <= 1}
      >
        最初
      </button>
      <button
        type="button"
        onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
        disabled={safePage <= 1}
      >
        前へ
      </button>
      <span>
        {safePage} / {totalPages} ページ（全{fmt(sortedList.length)}ユーザー中{" "}
        {fmt(paginatedList.length)}ユーザー表示）
      </span>
      <button
        type="button"
        onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
        disabled={safePage >= totalPages}
      >
        次へ
      </button>
      <button
        type="button"
        onClick={() => setCurrentPage(totalPages)}
        disabled={safePage >= totalPages}
      >
        最後
      </button>
      <select
        aria-label="1ページのユーザー数"
        value={pageSize}
        onChange={(e) => {
          setPageSize(Number(e.target.value));
          setCurrentPage(1);
        }}
      >
        {[50, 100, 200, 500].map((n) => (
          <option value={n} key={n}>
            {n}ユーザー
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <MainLayout>
      <style>{`
        .fans-page { color:#172334; }
        .fans-page h2 { margin:0 0 15px; color:#166534; font-size:19px; }
        .fans-toolbar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin:12px 0; }
        .fans-toolbar input { min-width:145px; flex:1 1 170px; padding:9px 10px; border:1px solid #cbd5e1; border-radius:7px; }
        .fans-page button, .fans-page select { padding:8px 10px; border:1px solid #cbd5e1; background:white; border-radius:7px; cursor:pointer; }
        .fans-page button:disabled { opacity:.45; cursor:not-allowed; }
        .fans-section { background:white; border:1px solid #e2e8f0; border-radius:12px; padding:16px; margin:16px 0; }
        .fans-section-head { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
        .fans-section-head h3 { font-size:16px; margin:0; }
        .fans-kpis { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:10px; margin-bottom:16px; }
        .fans-kpi { background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:14px; }
        .fans-kpi small { display:block; color:#64748b; font-size:12px; margin-bottom:6px; }
        .fans-kpi b { font-size:24px; font-variant-numeric:tabular-nums; }
        .fans-charts { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
        .fans-chart-panel { min-width:0; border:1px solid #e2e8f0; border-radius:10px; padding:12px; }
        .fans-chart-panel h4 { margin:0 0 8px; font-size:14px; }
        .fans-note { font-size:12px; color:#64748b; line-height:1.6; margin:7px 0; }
        .fans-pagination { display:flex; flex-wrap:wrap; gap:8px; align-items:center; justify-content:center; padding:15px 0; font-size:13px; }
        .fans-page th[aria-sort] { cursor:pointer; user-select:none; }
        .fans-table-wrap { overflow-x:auto; }
        .fans-page table { min-width:620px; }
        @media(max-width:850px) { .fans-kpis { grid-template-columns:repeat(2,minmax(0,1fr)); } .fans-charts { grid-template-columns:1fr; } }
        @media(max-width:480px) { .fans-section { padding:11px; } .fans-kpi b { font-size:20px; } }
      `}</style>
      <div className="fans-page">
        <h2>ファン-お気に入り一覧</h2>
        {loading ? (
          <div role="status" style={{ padding: 40, textAlign: "center" }}>
            読み込み中...
          </div>
        ) : errorMessage ? (
          <p role="alert" style={{ color: "#b91c1c" }}>
            {errorMessage}
          </p>
        ) : (
          <>
            <section className="fans-section" aria-label="お気に入り分析">
              <div className="fans-section-head">
                <div>
                  <h3>お気に入り分析</h3>
                  <p className="fans-note">現在の絞り込み結果を集計・可視化</p>
                </div>
                <button
                  type="button"
                  onClick={() => setChartsVisible((v) => !v)}
                >
                  {chartsVisible ? "グラフを閉じる" : "グラフを表示"}
                </button>
              </div>
              <div className="fans-kpis">
                <div className="fans-kpi">
                  <small>お気に入り登録数</small>
                  <b>{fmt(analytics.total)}</b>
                </div>
                <div className="fans-kpi">
                  <small>対象プロ／ゲスト数</small>
                  <b>{fmt(analytics.groups)}</b>
                </div>
                <div className="fans-kpi">
                  <small>ユニークファン数</small>
                  <b>{fmt(analytics.uniqueFans)}</b>
                </div>
                <div className="fans-kpi">
                  <small>1ファンあたり平均登録数</small>
                  <b>{analytics.averagePerFan}</b>
                </div>
              </div>
              {chartsVisible && (
                <>
                  <div className="fans-charts">
                    <div className="fans-chart-panel">
                      <h4>お気に入り登録数 TOP10（プロ／ゲスト別）</h4>
                      <div style={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={analytics.topPros}
                            layout="vertical"
                            margin={{ left: 10, right: 24, top: 8, bottom: 6 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              horizontal={false}
                            />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={105}
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) =>
                                v.length > 12 ? `${v.slice(0, 12)}…` : v
                              }
                            />
                            <Tooltip
                              formatter={(value) => [
                                `${fmt(value)} 件`,
                                "お気に入り登録数",
                              ]}
                              labelFormatter={(_label, payload) =>
                                payload?.[0]?.payload?.name || _label
                              }
                            />
                            <Bar
                              dataKey="count"
                              fill="#2563eb"
                              radius={[0, 5, 5, 0]}
                              maxBarSize={22}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="fans-chart-panel">
                      <h4>プロ／ゲスト別 登録数の分布</h4>
                      <div style={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={analytics.buckets}
                            margin={{ left: 0, right: 8, top: 8, bottom: 6 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} width={38} />
                            <Tooltip
                              formatter={(value) => [
                                `${fmt(value)} ユーザー`,
                                "プロ／ゲスト数",
                              ]}
                            />
                            <Bar
                              dataKey="count"
                              fill="#0d9488"
                              radius={[5, 5, 0, 0]}
                              maxBarSize={52}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="fans-note" style={{ marginTop: 12 }}>
                    ファン名はシステム上で重複登録不可のため、同名は同じファンとして扱います。お気に入り登録数は登録関係の件数、ユニークファン数はファン名の重複を除いた人数です。1ファンあたり平均登録数は登録件数÷ユニークファン数です。
                    {analytics.unnamed > 0
                      ? ` ファン名未設定：${fmt(analytics.unnamed)}件。`
                      : ""}
                  </div>
                  {analytics.topFans.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <h4 style={{ fontSize: 14, margin: "0 0 8px" }}>
                        お気に入り登録数が多いファン TOP10
                      </h4>
                      <div className="fans-table-wrap">
                        <table
                          style={{
                            width: "100%",
                            minWidth: 400,
                            borderCollapse: "collapse",
                            fontSize: 13,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: 8,
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                ファン名
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: 8,
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                登録件数
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.topFans.map((item) => (
                              <tr key={item.name}>
                                <td
                                  style={{
                                    padding: 8,
                                    borderBottom: "1px solid #e2e8f0",
                                  }}
                                >
                                  {item.name}
                                </td>
                                <td
                                  style={{
                                    padding: 8,
                                    textAlign: "right",
                                    borderBottom: "1px solid #e2e8f0",
                                  }}
                                >
                                  {fmt(item.count)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            <section className="fans-section" aria-label="お気に入り明細">
              <div className="fans-section-head">
                <h3>プロ／ゲスト＋ファン一覧</h3>
                <span className="fans-note">
                  全登録：{fmt(totalFans)}件 ／ フィルター結果：
                  {fmt(analytics.total)}件
                </span>
              </div>
              <div className="fans-toolbar">
                <input
                  type="text"
                  placeholder="プロ／ゲスト名"
                  aria-label="プロ／ゲスト名で検索"
                  value={filterUserName}
                  onChange={(e) => setFilterUserName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="カナ／検索キーワード"
                  aria-label="カナ／検索キーワードで検索"
                  value={filterKanaSearchKeyWord}
                  onChange={(e) => setFilterKanaSearchKeyWord(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="ファン名"
                  aria-label="ファン名で検索"
                  value={filterFanName}
                  onChange={(e) => setFilterFanName(e.target.value)}
                />
                <button type="button" onClick={clear}>
                  クリア
                </button>
                <button
                  type="button"
                  onClick={exportToExcel}
                  disabled={!sortedList.length}
                >
                  Excel出力
                </button>
              </div>
              <p className="fans-note">
                各項目に「無」を入力すると未設定のデータを検索します。列名をクリックすると昇順・降順で並べ替えます。
              </p>
              <Pagination />
              <div className="fans-table-wrap">
                <Table>
                  <thead>
                    <tr>
                      <TableHeader
                        role="button"
                        tabIndex={0}
                        aria-sort={
                          sortColumn === "user_name"
                            ? sortDirection === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                        onClick={() => handleSort("user_name")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSort("user_name");
                          }
                        }}
                      >
                        プロ／ゲスト{sortIcon("user_name")}
                      </TableHeader>
                      <TableHeader
                        role="button"
                        tabIndex={0}
                        aria-sort={
                          sortColumn === "kana"
                            ? sortDirection === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                        onClick={() => handleSort("kana")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSort("kana");
                          }
                        }}
                      >
                        カナ／検索キーワード{sortIcon("kana")}
                      </TableHeader>
                      <TableHeader
                        role="button"
                        tabIndex={0}
                        aria-sort={
                          sortColumn === "fanName"
                            ? sortDirection === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                        onClick={() => handleSort("fanName")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSort("fanName");
                          }
                        }}
                      >
                        ファン名{sortIcon("fanName")}
                      </TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {!paginatedList.length ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          該当するデータがありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedList.map((item, index) => (
                        <React.Fragment
                          key={`${text(item.user_id) || text(item.user_name)}-${(safePage - 1) * pageSize + index}`}
                        >
                          <TableRow>
                            <TableCell rowSpan={item.fansList.length + 1}>
                              {text(item.user_name) || "—"}
                            </TableCell>
                            <TableCell rowSpan={item.fansList.length + 1}>
                              {kanaKeyword(item) || "—"}
                            </TableCell>
                          </TableRow>
                          {item.fansList.map((fan, fanIndex) => (
                            <TableRow key={`${fanIndex}-${text(fan?.fanName)}`}>
                              <TitleCell>{text(fan?.fanName) || "—"}</TitleCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <Pagination />
            </section>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Fans;
